import { Injectable } from '@angular/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

export type OEPGqlScalar = string | number | boolean | null;
export interface OEPGqlVariables { readonly [key: string]: OEPGqlScalar | OEPGqlVariables | readonly OEPGqlVariables[] | undefined }

/**
 * The one seam OEP's custom GraphQL calls go through (the file mutations and RunAction).
 * Production binding is MJ's own client, GraphQLDataProvider.ExecuteGQL — the configured
 * provider's graphql-request client, with MJ's JWT_EXPIRED refresh-and-retry. It rethrows a
 * graphql-request ClientError whose `response.errors[0].extensions.code` carries the server's
 * refusal code (graphql-dataprovider dist/index.mjs, `async ExecuteGQL`). Abstract so specs
 * substitute a fake without a provider.
 */
@Injectable({ providedIn: 'root', useFactory: () => new MJGraphQLTransport() })
export abstract class OEPGraphQLTransport {
    /** T is the caller's declaration of the operation's `data` shape (the server schema is the contract). */
    abstract Execute<T>(query: string, variables: OEPGqlVariables): Promise<T>;
}

export class MJGraphQLTransport extends OEPGraphQLTransport {
    async Execute<T>(query: string, variables: OEPGqlVariables): Promise<T> {
        const data: T = await GraphQLDataProvider.ExecuteGQL(query, variables, true);
        return data;
    }
}

/** A GraphQL error's code and message, when `err` is a graphql-request ClientError. */
export interface OEPGqlErrorInfo { Code: string | null; Message: string }

function isRecord(v: unknown): v is Record<string, unknown> { // unknown: narrowing a caught value
    return typeof v === 'object' && v !== null;
}

/** Reads `response.errors[0]` from a thrown ClientError; null when `err` is not one (network failure, programming error). */
export function ReadGraphQLError(err: unknown): OEPGqlErrorInfo | null { // unknown: catch clauses bind unknown
    if (!isRecord(err) || !isRecord(err['response'])) return null;
    const errors = err['response']['errors'];
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const first: unknown = errors[0]; // unknown: untyped JSON from the wire
    if (!isRecord(first)) return null;
    const ext = first['extensions'];
    const code = isRecord(ext) && typeof ext['code'] === 'string' ? ext['code'] : null;
    const message = typeof first['message'] === 'string' ? first['message'] : 'The request failed.';
    return { Code: code, Message: message };
}
